import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Icon from '../../../icons';
import * as CommonUtil from '../../../common/util';

const WATERMARKS = {
  US: { zip: "Zip", state: "State", county: "County" },
  CA: { zip: "Zip", state: "Provinces", county: "County" },
  GR: { zip: "Zip", state: "Peripheries", county: "Prefectures" },
  IN: { zip: "Zip", state: "State", county: "Taluks" },
  KE: { zip: "Zip", state: "Mkoa", county: "Kata" },
  GB: { zip: "Postal Code", state: "County", county: "District" },
};

const EMPTY_ADDRESS = { Address1: "", Address2: "", City: "", State: "", County: "", Zip: "" };

const COUNTRY_OPTIONS = CommonUtil.sortArrayByKey(
  [
    { value: "US", label: "United States" },
    { value: "CA", label: "Canada" },
    { value: "GR", label: "Greece" },
    { value: "IN", label: "India" },
    { value: "KE", label: "Kenya" },
    { value: "GB", label: "United Kingdom" },
  ],
  'label'
);

const REQUIRED_FIELDS = ["Zip", "Address1", "State", "City", "County"];

const Address = (props) => {
  const [zipWM, setZipWM] = useState("Zip");
  const [stateWM, setStateWM] = useState("State");
  const [countyWM, setCountyWM] = useState("County");
  const [cityWM] = useState("City"); // never updated anywhere in the original either — WATERMARKS has no "city" entry, so this stays fixed.
  const [selectedCountry, setSelectedCountry] = useState("");
  const [addressInfo, setAddressInfo] = useState(props.addressInfo);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (props.selectedCountry) {
      setSelectedCountry(props.selectedCountry);
    }
  }, []);

  const prevZipLocSuggestionRef = useRef(props.zipLocSuggestion);
  useEffect(() => {
    const prevZipLocSuggestion = prevZipLocSuggestionRef.current;
    prevZipLocSuggestionRef.current = props.zipLocSuggestion;

    if (prevZipLocSuggestion === props.zipLocSuggestion) return;
    if (!(props.zipLocSuggestion?.length > 0)) return;

    const suggestion = props.zipLocSuggestion[0];
    const updatedFields = {
      State: suggestion.State || "",
      County: suggestion.County || "",
      City: suggestion.City || "",
    };

    setAddressInfo((prev) => ({ ...prev, ...updatedFields }));
    setValidationErrors((prev) =>
      prev.filter((field) => {
        if (!(field in updatedFields)) return true;
        return updatedFields[field].trim() === ""; 
      })
    );
  }, [props.zipLocSuggestion]);

  const removeErrorField = (key) => {
    setValidationErrors((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : prev));
  };

  const validateAddressField = (info) => {
    const errorFields = REQUIRED_FIELDS.filter((field) => info[field].trim() === "");
    if (errorFields.length > 0) {
      setValidationErrors(errorFields);
      return false;
    }
    return true;
  };

  const evaluateSaveButtonEnabled = (info) => {
    const key = "businessLocation";
    validateAddressField(info);

    const hasAnyValue = Object.keys(info).some((k) => info[k].trim() !== "");
    const hasAllEmpty = Object.keys(info).every((k) => info[k].trim() === "");

    if (hasAnyValue && !props.inputChange.includes(key)) {
      props.updateInputchange([...props.inputChange, key]);
    } else if (hasAllEmpty && props.inputChange.includes(key)) {
      props.updateInputchange(props.inputChange.filter((item) => item !== key));
    }
  };

  const handleWatermark = (value) => {
    setZipWM(WATERMARKS[value].zip);
    setStateWM(WATERMARKS[value].state);
    setCountyWM(WATERMARKS[value].county);
    setAddressInfo(EMPTY_ADDRESS);
    setSelectedCountry(value);
    evaluateSaveButtonEnabled(EMPTY_ADDRESS);
  };

  const changeAddressInfo = ({ key, value }) => {
    const updated = { ...addressInfo, [key]: value };
    setAddressInfo(updated);
    if (key === "Zip") {
      props.getlocationByZip({ q: value, c: selectedCountry });
    }
    removeErrorField(key);
    evaluateSaveButtonEnabled(updated);
  };

  const submitAddressUpdate = () => {
    if (!props.inputChange.includes("businessLocation")) return;
    if (!validateAddressField(addressInfo)) return;

    props.updateBusinessLoc({
      address1: addressInfo.Address1.trim(),
      address2: addressInfo.Address2.trim(),
      city: addressInfo.City.trim(),
      state: addressInfo.State.trim(),
      zip: addressInfo.Zip.trim(),
      county: addressInfo.County.trim(),
      country: selectedCountry,
    });
  };

  return (
    <div className="gridOdd col-md-9">
      <div className="colFlex">
        <div className="form-row">
          <div className="col-md-6">
            <div className="formGroup">
              <select className="span12" value={selectedCountry} onChange={(e) => handleWatermark(e.target.value)}>
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="col-md-6">
            <div className={validationErrors.includes("Address1") ? "formGroup colFlex errorMsg" : "formGroup"}>
              <input
                type="text"
                className="span12"
                value={addressInfo.Address1}
                placeholder="Address 1"
                maxLength="200"
                onChange={(e) => changeAddressInfo({ key: "Address1", value: e.target.value })}
              />
              {validationErrors.includes("Address1") && <span className="errorMsg">Address 1 is required.</span>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="formGroup">
              <input
                type="text"
                className="span12"
                placeholder="Address 2"
                value={addressInfo.Address2}
                maxLength="200"
                onChange={(e) => changeAddressInfo({ key: "Address2", value: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="col-md-3">
            <div className={validationErrors.includes("Zip") ? "formGroup colFlex errorMsg" : "formGroup"}>
              <input
                type="text"
                className="span12"
                placeholder={zipWM}
                value={addressInfo.Zip}
                onChange={(e) => changeAddressInfo({ key: "Zip", value: e.target.value })}
                onBlur={(e) => props.getlocationByZip({ q: e.target.value, c: selectedCountry })}
              />
              {validationErrors.includes("Zip") && <span className="errorMsg">{`${zipWM} is required.`}</span>}
            </div>
          </div>
          <div className="col-md-3">
            <div className={validationErrors.includes("State") ? "formGroup colFlex errorMsg" : "formGroup"}>
              <input
                type="text"
                className="span12"
                placeholder={stateWM}
                value={addressInfo.State}
                onChange={(e) => changeAddressInfo({ key: "State", value: e.target.value })}
              />
              {validationErrors.includes("State") && <span className="errorMsg">{`${stateWM} is required.`}</span>}
            </div>
          </div>
          <div className="col-md-3">
            <div className={validationErrors.includes("County") ? "formGroup colFlex errorMsg" : "formGroup"}>
              <input
                type="text"
                className="span12"
                placeholder={countyWM}
                value={addressInfo.County}
                onChange={(e) => changeAddressInfo({ key: "County", value: e.target.value })}
              />
              {validationErrors.includes("County") && <span className="errorMsg">{`${countyWM} is required.`}</span>}
            </div>
          </div>
          <div className="col-md-3">
            <div className={validationErrors.includes("City") ? "formGroup colFlex errorMsg" : "formGroup"}>
              <input
                type="text"
                className="span12"
                placeholder={cityWM}
                value={addressInfo.City}
                onChange={(e) => changeAddressInfo({ key: "City", value: e.target.value })}
              />
              {validationErrors.includes("City") && <span className="errorMsg">{`${cityWM} is required.`}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="rowFlex">
        <span className="mr">
        <button className="mr btn-cc btn-cc__transparent" onClick={submitAddressUpdate}>
          <Icon name="check" className={`icon ${props.inputChange.includes('businessLocation') ? "activeIcon" : "disabled"} check`} />
        </button>
        </span>
        <button className="btn-cc btn-cc__transparent" onClick={() => props.closeEditField()}>
          <Icon name="close" className="icon" />
        </button>
      </div>
    </div>
  );
};

export default Address;